import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  userService,
  User,
  OrganizationOverview,
} from "../services/UserService";
import { healthService, HealthStatus } from "../services/HealthService";
import BackendUnavailable from "../components/layout/BackendUnavailable";
import { systemConfigService, SystemConfig } from "../services/SystemService";
import MaintenanceMode from "../components/layout/MaintenanceMode";
import { AppConfig } from "../utils/Constants";
import ForgeworksLoader from "../components/common/ForgeworksLoader";
import { setSentryUser, clearSentryUser } from "../utils/sentry";
interface AuthContextType {
  isLoggedIn: boolean;
  loading: boolean;
  userProfile?: User;
  organizationOverview?: OrganizationOverview;
  healthStatus?: HealthStatus;
  systemConfig?: SystemConfig;
  login: () => void;
  dataRefresh: () => Promise<void>;
  fetchUserProfile: () => void;
  fetchOrganizationOverview: () => void;
  loginFromRegistration: () => void;
  logout: () => void;
  setUserProfile: (profile: User) => void;
  refreshAuthToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [userProfile, setUserProfile] = useState<User | undefined>();
  const [isRetrying, setIsRetrying] = useState(false);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | undefined>();
  const [organizationOverview, setOrganizationOverview] = useState<
    OrganizationOverview | undefined
  >();
  const [healthStatus, setHealthStatus] = useState<HealthStatus | undefined>();
  const MINIMUM_LOADER_TIME = 500;

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUserProfile(undefined);
    setOrganizationOverview(undefined);
    setSystemConfig(undefined);
    setHealthStatus({ dbState: "connected" } as HealthStatus);
    clearSentryUser();
  }, []);

  const fetchSystemConfig = useCallback(async () => {
    try {
      const { data } = await systemConfigService.getSystemConfig();
      AppConfig.updateConfig({
        LOGO_URL: data.logoURL,
        BACKEND_UNAVAILABLE_MESSAGE: data.maintenanceMessage,
      });
      setSystemConfig(data);
    } catch (error) {
      console.error("Error fetching system configuration:", error);
    }
  }, []);

  const fetchHealthStatus = useCallback(async () => {
    try {
      const { data } = await healthService.getDatabaseStatus();
      setHealthStatus(data);
      return data.dbState === "connected";
    } catch (error) {
      console.error("Error fetching health status:", error);
      setHealthStatus({ dbState: "disconnected" } as HealthStatus);
      return false;
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      const user = await userService.getCurrentUser();
      setUserProfile(user);
      if (user) {
        setSentryUser({
          id: user.accountId,
          email: user.email,
          username: user.email,
        });
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrganizationOverview = useCallback(async () => {
    try {
      const { data } = await userService.getOrganizationOverview();
      setOrganizationOverview(data);
    } catch (error) {
      console.error("Error fetching organization overview:", error);
    }
  }, []);

  const refreshAuthToken = useCallback(async (): Promise<boolean> => {
    try {
      await userService.refreshToken();
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const checkSession = useCallback(async (): Promise<boolean> => {
    try {
      const { authenticated } = await userService.checkSession();
      return authenticated;
    } catch (error) {
      console.error("Session check failed:", error);
      return false;
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      console.log("Initializing app...");
      setLoading(true);

      await fetchSystemConfig();
      console.log("Fetched system config");

      if (systemConfig?.maintenanceMode) {
        setInitializing(false);
        setLoading(false);
        return;
      }

      const backendIsHealthy = await fetchHealthStatus();
      console.log("Backend health:", backendIsHealthy);

      if (!backendIsHealthy) {
        setInitializing(false);
        setLoading(false);
        return;
      }

      const isAuthenticated = await checkSession();
      console.log("Session authenticated:", isAuthenticated);

      if (isAuthenticated) {
        setIsLoggedIn(true);
        await Promise.all([fetchUserProfile(), fetchOrganizationOverview()]);
      } else {
        setIsLoggedIn(false);
      }

      setTimeout(() => {
        setInitializing(false);
        setLoading(false);
        console.log("Initialization complete");
      }, MINIMUM_LOADER_TIME);
    };

    initializeApp();
  }, [
    fetchSystemConfig,
    fetchHealthStatus,
    checkSession,
    fetchUserProfile,
    fetchOrganizationOverview,
  ]);

  const login = useCallback(async () => {
    setIsLoggedIn(true);
    await fetchUserProfile();
    await fetchOrganizationOverview();
  }, [fetchUserProfile, fetchOrganizationOverview]);

  const dataRefresh = async (): Promise<void> => {
    try {
      await Promise.all([fetchUserProfile(), fetchOrganizationOverview()]);
    } catch (error) {
      console.error("Error during data refresh:", error);
    }
  };

  const loginFromRegistration = useCallback(async () => {
    setIsLoggedIn(true);
    await fetchUserProfile();
    await fetchOrganizationOverview();
  }, [fetchUserProfile, fetchOrganizationOverview]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        loading,
        systemConfig,
        userProfile,
        organizationOverview,
        healthStatus,
        dataRefresh,
        login,
        fetchUserProfile,
        fetchOrganizationOverview,
        loginFromRegistration,
        logout,
        setUserProfile,
        refreshAuthToken,
      }}
    >
      {initializing ? (
        <ForgeworksLoader />
      ) : systemConfig?.maintenanceMode ? (
        <MaintenanceMode message={systemConfig.maintenanceMessage} />
      ) : healthStatus?.dbState !== "connected" ? (
        <BackendUnavailable
          message={AppConfig.BACKEND_UNAVAILABLE_MESSAGE}
          onRetry={async () => {
            setIsRetrying(true);
            try {
              const backendIsHealthy = await fetchHealthStatus();
              if (backendIsHealthy) {
                await Promise.all([
                  fetchUserProfile(),
                  fetchOrganizationOverview(),
                ]);
              }
            } catch (error) {
              console.error("Retry failed:", error);
            } finally {
              setTimeout(() => setIsRetrying(false), 1000);
            }
          }}
          isRetrying={isRetrying}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
